import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiClientError } from "@/lib/api";
import { displayTitle, fmtDateOnly, fmtFileSize, fmtRelative } from "@/lib/format";
import { log } from "@/lib/logger";
import { usePreferences } from "@/lib/preferences";
import { useLongPress } from "@/hooks/useLongPress";
import { ImportModal } from "@/components/ImportModal";
import { SkillImportModal } from "@/components/SkillImportModal";
import { NewReadmeChooserModal } from "@/components/NewReadmeChooserModal";
import { MetadataWizardModal } from "@/components/MetadataWizardModal";
import { READMECATEGORIES } from "@shared/types";
import { downloadText, downloadZip, sanitizeFilename } from "@/lib/download";
// `pointer: coarse` is the standard touch UI signal — it covers phones and
// tablets without misfiring on hybrid laptops with both pointers.
function useIsTouchUi() {
    const [isTouch, setIsTouch] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia)
            return;
        const media = window.matchMedia("(pointer: coarse)");
        const update = () => setIsTouch(media.matches);
        update();
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, []);
    return isTouch;
}
// ── helpers ──────────────────────────────────────────────────────────────────
function timeFilterCutoff(tf) {
    const now = Math.floor(Date.now() / 1000);
    if (tf === "1h")
        return now - 3600;
    if (tf === "1d")
        return now - 86400;
    if (tf === "1w")
        return now - 7 * 86400;
    return 0;
}
function sortReadmes(list, key) {
    return [...list].sort((a, b) => {
        if (key === "az")
            return displayTitle(a.title).localeCompare(displayTitle(b.title));
        if (key === "za")
            return displayTitle(b.title).localeCompare(displayTitle(a.title));
        if (key === "created")
            return b.createdAt - a.createdAt;
        return b.updatedAt - a.updatedAt;
    });
}
// Filters/sort/group/scroll for the readmes list page. Stashed in
// sessionStorage so navigating into a readme and back leaves the user where
// they were. Tab close still resets, which matches the typical user mental
// model of "filters are for this browsing session".
const LIST_STATE_KEY = "node-one.readmesList.state.v1";
const DEFAULT_LIST_STATE = {
    filter: "all",
    query: "",
    sortKey: "updated",
    timeFilter: "all",
    showTemplatesOnly: false,
    showNoTagsOnly: false,
    groupByCategory: true,
    activeTag: null,
    scrollY: 0,
};
function readListState() {
    if (typeof window === "undefined")
        return DEFAULT_LIST_STATE;
    try {
        const raw = window.sessionStorage.getItem(LIST_STATE_KEY);
        if (!raw)
            return DEFAULT_LIST_STATE;
        return { ...DEFAULT_LIST_STATE, ...JSON.parse(raw) };
    }
    catch {
        return DEFAULT_LIST_STATE;
    }
}
function writeListState(state) {
    if (typeof window === "undefined")
        return;
    try {
        window.sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify(state));
    }
    catch {
        // sessionStorage may be unavailable (private mode quirks); ignore.
    }
}
// ── sub-components ────────────────────────────────────────────────────────────
// Confirmation modal for destructive actions. Replaces the synchronous
// `window.confirm()` which looks like a system alert on desktop and is hard
// to dismiss safely on phones (the OK button is often near the trash icon).
function ConfirmModal({ title, message, confirmLabel = "Delete", destructive = true, busy = false, onConfirm, onCancel, }) {
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape" && !busy)
                onCancel();
            if (e.key === "Enter" && !busy)
                onConfirm();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [busy, onCancel, onConfirm]);
    return (_jsx("div", { className: "fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4", children: _jsxs("div", { className: "card w-full max-w-sm rounded-b-none p-6 sm:rounded-lg", children: [_jsx("h2", { className: "text-lg font-medium", children: title }), _jsx("div", { className: "mt-2 text-sm text-slate-600 dark:text-slate-300", children: message }), _jsxs("div", { className: "mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", children: [_jsx("button", { className: "btn-secondary", onClick: onCancel, disabled: busy, children: "Cancel" }), _jsx("button", { className: destructive ? "btn-danger" : "btn-primary", onClick: onConfirm, disabled: busy, autoFocus: true, children: busy ? "…" : confirmLabel })] })] }) }));
}
// Per-item context menu. On desktop it's a positioned dropdown near the
// pointer; on touch UIs it slides up from the bottom as an action sheet so
// the targets are big enough for a thumb and the chrome feels native.
function ContextMenu({ state, isTouchUi, onOpen, onCopy, onDownload, onRename, onDelete, onClose, }) {
    const ref = useRef(null);
    useEffect(() => {
        function handler(e) {
            if (ref.current && !ref.current.contains(e.target))
                onClose();
        }
        document.addEventListener("mousedown", handler);
        document.addEventListener("touchstart", handler);
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("touchstart", handler);
        };
    }, [onClose]);
    if (isTouchUi) {
        return (_jsx("div", { className: "fixed inset-0 z-[60] flex items-end justify-center bg-black/40", children: _jsxs("div", { ref: ref, className: "w-full animate-[slideUp_180ms_ease-out] rounded-t-2xl border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] shadow-xl dark:border-slate-700 dark:bg-slate-900", children: [_jsx("div", { className: "mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-600" }), _jsx("div", { className: "truncate px-4 pb-2 pt-1 text-xs text-slate-500 dark:text-slate-400", children: displayTitle(state.readme.title) }), _jsx(SheetItem, { icon: _jsx(IconExternal, {}), label: "Open", onClick: () => { onOpen(); onClose(); } }), _jsx(SheetItem, { icon: _jsx(IconCopy, {}), label: "Copy", onClick: () => { onCopy(); onClose(); } }), _jsx(SheetItem, { icon: _jsx(IconDownload, {}), label: "Download", onClick: () => { onDownload(); onClose(); } }), _jsx(SheetItem, { icon: _jsx(IconPencil, {}), label: "Rename", onClick: () => { onRename(); onClose(); } }), _jsx(SheetItem, { icon: _jsx(IconTrash, {}), label: "Delete", destructive: true, onClick: () => { onDelete(); onClose(); } }), _jsx("button", { className: "mb-2 mt-1 w-full border-t border-slate-200 px-4 py-3 text-center text-sm font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300", onClick: onClose, children: "Cancel" })] }) }));
    }
    // Desktop dropdown — anchored to pointer, clamped on-screen.
    const style = { position: "fixed", zIndex: 60 };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const menuW = 180;
    const menuH = 168;
    style.top = Math.min(state.y, vh - menuH - 8);
    style.left = Math.min(state.x, vw - menuW - 8);
    return (_jsxs("div", { ref: ref, style: style, className: "w-44 rounded-lg border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-900", children: [_jsx(DropdownItem, { icon: _jsx(IconExternal, {}), label: "Open", onClick: () => { onOpen(); onClose(); } }), _jsx(DropdownItem, { icon: _jsx(IconCopy, {}), label: "Copy", onClick: () => { onCopy(); onClose(); } }), _jsx(DropdownItem, { icon: _jsx(IconDownload, {}), label: "Download", onClick: () => { onDownload(); onClose(); } }), _jsx(DropdownItem, { icon: _jsx(IconPencil, {}), label: "Rename", onClick: () => { onRename(); onClose(); } }), _jsx("hr", { className: "my-1 border-slate-200 dark:border-slate-700" }), _jsx(DropdownItem, { icon: _jsx(IconTrash, {}), label: "Delete", destructive: true, onClick: () => { onDelete(); onClose(); } })] }));
}
function DropdownItem({ icon, label, onClick, destructive, }) {
    return (_jsxs("button", { className: `flex w-full items-center gap-2 px-4 py-2 text-sm ${destructive
            ? "text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/30"
            : "hover:bg-slate-100 dark:hover:bg-slate-800"}`, onClick: onClick, children: [icon, label] }));
}
function SheetItem({ icon, label, onClick, destructive, }) {
    return (_jsxs("button", { className: `flex w-full items-center gap-3 px-5 py-3.5 text-left text-base ${destructive
            ? "text-rose-600 active:bg-rose-50 dark:text-rose-400 dark:active:bg-rose-950/30"
            : "text-slate-800 active:bg-slate-100 dark:text-slate-100 dark:active:bg-slate-800"}`, onClick: onClick, children: [_jsx("span", { className: "shrink-0", children: icon }), _jsx("span", { children: label })] }));
}
function IconExternal() {
    return (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("path", { d: "M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M15 3h6v6M10 14L21 3", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }));
}
function IconCopy() {
    return (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("rect", { x: "9", y: "9", width: "13", height: "13", rx: "2", stroke: "currentColor", strokeWidth: "2" }), _jsx("path", { d: "M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })] }));
}
function IconPencil() {
    return (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("path", { d: "M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }));
}
function IconTrash() {
    return (_jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
function IconDownload() {
    return (_jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M12 15V3m0 12l-4-4m4 4l4-4M2 17l.621 2.485A2 2 0 004.561 21h14.878a2 2 0 001.94-1.515L22 17", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
function RenameModal({ readme, onSave, onClose, }) {
    const [value, setValue] = useState(displayTitle(readme.title));
    const [suggesting, setSuggesting] = useState(false);
    async function handleSuggest() {
        setSuggesting(true);
        try {
            const cfg = { provider: undefined, model: undefined };
            const res = await api.suggestTitle({ content: readme.title, ...cfg });
            setValue(displayTitle(res.title));
        }
        catch {
            // silent
        }
        finally {
            setSuggesting(false);
        }
    }
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4", children: _jsxs("div", { className: "card w-full max-w-sm p-6", children: [_jsx("h2", { className: "mb-4 text-lg font-medium", children: "Rename" }), _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { autoFocus: true, className: "input flex-1", value: value, onChange: (e) => setValue(e.target.value), onKeyDown: (e) => {
                                if (e.key === "Enter")
                                    onSave(value.trim() || readme.title);
                                if (e.key === "Escape")
                                    onClose();
                            } }), _jsx("button", { className: "btn-secondary shrink-0 text-xs", onClick: handleSuggest, disabled: suggesting, title: "Suggest name with AI", children: suggesting ? "…" : "✦" })] }), _jsxs("div", { className: "mt-4 flex justify-end gap-2", children: [_jsx("button", { className: "btn-secondary", onClick: onClose, children: "Cancel" }), _jsx("button", { className: "btn-primary", onClick: () => onSave(value.trim() || readme.title), children: "Save" })] })] }) }));
}
// ── swipe-to-delete card ──────────────────────────────────────────────────────
function ReadmeCard({ readme, onOpen, onDelete, onContextMenu, selectMode, isSelected, onSelect, derivedCount = 0, }) {
    const [swipeX, setSwipeX] = useState(0);
    const [dragging, setDragging] = useState(false);
    const swipeXRef = useRef(0); // mirrors swipeX but readable inside native handlers
    const draggingRef = useRef(false);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const touchStartTime = useRef(0);
    const isHorizontalSwipe = useRef(false);
    const hasHapticed = useRef(false);
    const cardRef = useRef(null);
    // Two-stage swipe: at REVEAL we surface the red Delete action, at COMMIT
    // we trigger the confirm modal directly because the user clearly committed
    // to the gesture (iOS Mail / Gmail use the same idea).
    const REVEAL_THRESHOLD = 80;
    const COMMIT_THRESHOLD = 180;
    const longPress = useLongPress((e) => {
        if (!selectMode)
            onContextMenu(e);
    }, 500);
    // Keep refs in sync so native event handlers always see current values.
    useEffect(() => { swipeXRef.current = swipeX; }, [swipeX]);
    useEffect(() => { draggingRef.current = dragging; }, [dragging]);
    // Capture selectMode in a ref so the non-passive handler always reads current.
    const selectModeRef = useRef(selectMode);
    useEffect(() => { selectModeRef.current = selectMode; }, [selectMode]);
    // Non-passive touchmove listener — needed so e.preventDefault() actually
    // suppresses the browser's native scroll while we're handling a horizontal
    // swipe. React attaches onTouchMove as passive by default (since React 17),
    // which makes preventDefault() a silent no-op and causes the page to scroll
    // and the card to swipe simultaneously.
    useEffect(() => {
        const el = cardRef.current;
        if (!el)
            return;
        function handleTouchMove(e) {
            if (selectModeRef.current)
                return;
            const dx = e.touches[0].clientX - touchStartX.current;
            const dy = e.touches[0].clientY - touchStartY.current;
            if (!isHorizontalSwipe.current) {
                if (Math.abs(dy) > Math.abs(dx) + 5)
                    return;
                if (Math.abs(dx) > 8) {
                    isHorizontalSwipe.current = true;
                    setDragging(true);
                    draggingRef.current = true;
                }
            }
            if (isHorizontalSwipe.current && dx < 0) {
                e.preventDefault();
                longPress.onTouchMove();
                const next = Math.max(dx, -240);
                // Haptic pulse once when crossing the commit threshold
                if (next <= -COMMIT_THRESHOLD && !hasHapticed.current) {
                    hasHapticed.current = true;
                    if (navigator.vibrate)
                        navigator.vibrate(10);
                }
                swipeXRef.current = next;
                setSwipeX(next);
            }
        }
        el.addEventListener("touchmove", handleTouchMove, { passive: false });
        return () => el.removeEventListener("touchmove", handleTouchMove);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [longPress]);
    function onTouchStart(e) {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
        touchStartTime.current = Date.now();
        isHorizontalSwipe.current = false;
        hasHapticed.current = false;
        setDragging(false);
        draggingRef.current = false;
        longPress.onTouchStart(e);
    }
    function onTouchEnd() {
        longPress.onTouchEnd();
        setDragging(false);
        draggingRef.current = false;
        if (selectModeRef.current)
            return;
        const x = swipeXRef.current;
        const elapsed = Date.now() - touchStartTime.current;
        const velocity = elapsed > 0 ? Math.abs(x) / elapsed : 0; // px/ms
        // Commit on full drag past threshold OR a fast flick with at least 40px
        if (x <= -COMMIT_THRESHOLD || (velocity > 0.5 && x < -40)) {
            setSwipeX(0);
            swipeXRef.current = 0;
            onDelete();
        }
        else if (x <= -REVEAL_THRESHOLD) {
            setSwipeX(-100);
            swipeXRef.current = -100;
        }
        else {
            setSwipeX(0);
            swipeXRef.current = 0;
        }
    }
    const revealed = !selectMode && swipeX <= -REVEAL_THRESHOLD;
    const committing = !selectMode && swipeX <= -COMMIT_THRESHOLD;
    const showCreatedDate = readme.createdAt !== readme.updatedAt;
    return (_jsxs("div", { ref: cardRef, className: "relative overflow-hidden rounded-lg", style: { touchAction: "pan-y" }, children: [!selectMode && (_jsx("button", { className: `absolute inset-y-0 right-0 flex items-center justify-center bg-rose-600 text-sm font-medium text-white transition-[width] duration-150 ${committing ? "w-full" : "w-24"}`, onClick: () => { setSwipeX(0); onDelete(); }, tabIndex: -1, children: "Delete" })), _jsxs("div", { className: `card group flex h-full flex-col transition-colors hover:border-slate-300 hover:shadow-md dark:hover:border-slate-600 ${isSelected ? "ring-2 ring-sky-500 dark:ring-sky-400" : ""}`, style: {
                    transform: selectMode ? undefined : `translateX(${swipeX}px)`,
                    transition: dragging
                        ? "none"
                        : "transform 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 150ms ease",
                    cursor: "pointer",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                }, onTouchStart: onTouchStart, onTouchEnd: onTouchEnd, onMouseDown: longPress.onMouseDown, onMouseUp: longPress.onMouseUp, onMouseMove: longPress.onMouseMove, onContextMenu: (e) => { if (!selectMode) {
                    e.preventDefault();
                    onContextMenu(e);
                } }, onClick: () => {
                    if (selectMode) {
                        onSelect();
                        return;
                    }
                    if (Math.abs(swipeX) > 5) {
                        setSwipeX(0);
                        return;
                    }
                    if (revealed) {
                        setSwipeX(0);
                        return;
                    }
                    onOpen();
                }, children: [_jsxs("div", { className: "flex flex-1 flex-col p-4", children: [_jsxs("div", { className: "mb-2 flex items-start justify-between gap-2", children: [selectMode && (_jsx("div", { className: `mr-1 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-150 ${isSelected
                                            ? "scale-110 border-sky-500 bg-sky-500 text-white dark:border-sky-400 dark:bg-sky-400"
                                            : "scale-100 border-slate-300 dark:border-slate-600"}`, children: isSelected && (_jsx("svg", { width: "10", height: "10", viewBox: "0 0 12 12", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M2 6l3 3 5-5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) })) })), _jsxs("h3", { className: "line-clamp-2 flex-1 text-[15px] font-semibold leading-snug text-slate-900 dark:text-slate-100", children: [readme.icon && _jsx("span", { className: "mr-1.5", children: readme.icon }), displayTitle(readme.title)] }), _jsxs("div", { className: "flex shrink-0 flex-wrap items-start gap-1", children: [readme.isTemplate && (_jsx("span", { className: "rounded-full bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-800 dark:bg-violet-950/40 dark:text-violet-300", children: "template" })), readme.parentTemplateId && (_jsx("span", { className: "rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300", children: "derived" })), readme.isPinned && (_jsx("span", { className: "rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300", children: "pinned" }))] })] }), _jsxs("div", { className: "mt-auto flex flex-wrap items-end gap-1 pt-3 text-xs text-slate-500 dark:text-slate-400", children: [readme.category && (_jsx("span", { className: "rounded-full bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800", children: readme.category })), readme.tags.slice(0, 3).map((t) => (_jsxs("span", { className: "rounded-full bg-sky-50 px-1.5 py-0.5 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300", children: ["#", t] }, t))), _jsxs("span", { className: "ml-auto flex flex-col items-end gap-0.5 text-right leading-tight", children: [_jsxs("span", { children: ["Updated ", fmtRelative(readme.updatedAt)] }), showCreatedDate && (_jsxs("span", { className: "text-[11px] text-slate-400 dark:text-slate-500", children: ["Created ", fmtDateOnly(readme.createdAt)] })), readme.contentLength !== undefined && (_jsxs("span", { className: "text-[11px] text-slate-400 dark:text-slate-500", children: [fmtFileSize(readme.contentLength), " \u00B7 ", readme.lineCount ?? 1, " lines"] }))] })] })] }), readme.isTemplate && !selectMode && derivedCount > 0 && (_jsxs("div", { className: "border-t border-slate-100 px-3 py-1.5 text-xs text-slate-500 dark:border-slate-700", children: [derivedCount, " derived"] }))] })] }));
}
// ── skill card ────────────────────────────────────────────────────────────────
function SkillCard({ skill, onOpen, onDelete, }) {
    return (_jsxs("div", { className: "card flex h-full flex-col p-4 cursor-pointer", onClick: onOpen, children: [_jsxs("div", { className: "mb-2 flex items-start gap-2", children: [_jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", className: "mt-0.5 shrink-0 text-indigo-500", "aria-hidden": true, children: _jsx("path", { d: "M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z", stroke: "currentColor", strokeWidth: "2", strokeLinejoin: "round" }) }), _jsx("h3", { className: "flex-1 font-medium line-clamp-2", children: skill.title }), _jsx("button", { onClick: (e) => { e.stopPropagation(); onDelete(); }, className: "shrink-0 rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/30 dark:hover:text-rose-400", "aria-label": "Delete skill", children: _jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) })] }), skill.description && (_jsx("p", { className: "mb-2 line-clamp-2 text-xs text-slate-500 dark:text-slate-400", children: skill.description })), _jsxs("div", { className: "mt-auto flex items-end justify-between text-xs text-slate-500", children: [_jsx("span", { className: "font-mono", children: skill.name }), _jsxs("span", { className: "flex flex-col items-end gap-0.5 text-right leading-tight", children: [_jsxs("span", { children: ["Updated ", fmtRelative(skill.updatedAt)] }), skill.fileCount !== undefined && (_jsxs("span", { className: "text-[11px] text-slate-400 dark:text-slate-500", children: [skill.fileCount, " ", skill.fileCount === 1 ? "file" : "files", " \u00B7 ", fmtFileSize(skill.totalSize ?? 0)] }))] })] })] }));
}
// ── template derived row ──────────────────────────────────────────────────────
function TemplateDerivedRow({ items, onOpen, onDelete, onContextMenu, selectMode, selected, onSelect, }) {
    if (items.length === 0)
        return null;
    return (_jsx("div", { className: "mt-2 grid gap-2 border-l-2 border-slate-100 pl-3 sm:grid-cols-2 lg:grid-cols-3 dark:border-slate-800", children: items.map((child) => (_jsx(ReadmeCard, { readme: child, onOpen: () => onOpen(`/readmes/${child.id}`), onDelete: () => onDelete(child), onContextMenu: (e) => onContextMenu(child, e), selectMode: selectMode, isSelected: selected.has(child.id), onSelect: () => onSelect(child.id) }, child.id))) }));
}
// ── main list ─────────────────────────────────────────────────────────────────
export function ReadmesList() {
    const navigate = useNavigate();
    const { prefs, setPref } = usePreferences();
    // Read sessionStorage once on mount so filter/sort/scroll survive returning
    // from a detail page. Captured up front; subsequent persistence happens via
    // the effect below.
    const initialListState = useMemo(readListState, []);
    const [readmes, setReadmes] = useState(null);
    const [skills, setSkills] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [filter, setFilter] = useState(initialListState.filter);
    const [query, setQuery] = useState(initialListState.query);
    const [creating, setCreating] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [showSkillImport, setShowSkillImport] = useState(false);
    const [droppedImportFiles, setDroppedImportFiles] = useState(undefined);
    const [pageIsDragOver, setPageIsDragOver] = useState(false);
    const pageDragCounterRef = useRef(0);
    const [showChooser, setShowChooser] = useState(false);
    const [wizardSeed, setWizardSeed] = useState(null);
    const [showTemplatesOnly, setShowTemplatesOnly] = useState(initialListState.showTemplatesOnly);
    const [sortKey, setSortKey] = useState(initialListState.sortKey);
    const [timeFilter, setTimeFilter] = useState(initialListState.timeFilter);
    const [contextMenu, setContextMenu] = useState(null);
    const [renameTarget, setRenameTarget] = useState(null);
    const [showNoTagsOnly, setShowNoTagsOnly] = useState(initialListState.showNoTagsOnly);
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [deleting, setDeleting] = useState(false);
    const [bulkDownloading, setBulkDownloading] = useState(false);
    const [groupByCategory, setGroupByCategory] = useState(initialListState.groupByCategory);
    const [activeTag, setActiveTag] = useState(initialListState.activeTag);
    const [newCategory, setNewCategory] = useState("");
    const [showNewCategory, setShowNewCategory] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const isTouchUi = useIsTouchUi();
    const allCategories = [
        ...READMECATEGORIES,
        ...prefs.customCategories,
    ];
    useEffect(() => { void load(); }, []);
    // Global drag-to-import: dragging files anywhere on the page opens the
    // import modal and auto-processes the dropped files. We use a counter so
    // the overlay doesn't flicker when the pointer moves between child elements.
    useEffect(() => {
        function isFileDrag(e) {
            // Mac Safari may report an empty types list or use "public.file-url"
            // instead of "Files" during dragenter, so accept any of these.
            const types = e.dataTransfer?.types ?? [];
            return types.length === 0 || types.includes("Files") || types.includes("public.file-url");
        }
        function onDragEnter(e) {
            if (!isFileDrag(e))
                return;
            e.preventDefault();
            pageDragCounterRef.current += 1;
            setPageIsDragOver(true);
        }
        function onDragLeave(e) {
            if (!isFileDrag(e))
                return;
            pageDragCounterRef.current -= 1;
            if (pageDragCounterRef.current <= 0) {
                pageDragCounterRef.current = 0;
                setPageIsDragOver(false);
            }
        }
        function onDragOver(e) {
            if (isFileDrag(e))
                e.preventDefault();
        }
        function onDrop(e) {
            e.preventDefault();
            pageDragCounterRef.current = 0;
            setPageIsDragOver(false);
            // Prefer items API — dataTransfer.files can be empty on Mac for some
            // drag sources (e.g. Finder folder drops on Safari).
            const dropped = [];
            if (e.dataTransfer?.items) {
                for (const item of Array.from(e.dataTransfer.items)) {
                    if (item.kind === "file") {
                        const f = item.getAsFile();
                        if (f)
                            dropped.push(f);
                    }
                }
            }
            if (dropped.length === 0) {
                dropped.push(...Array.from(e.dataTransfer?.files ?? []));
            }
            if (dropped.length === 0)
                return;
            setDroppedImportFiles(dropped);
            setShowImport(true);
        }
        document.addEventListener("dragenter", onDragEnter);
        document.addEventListener("dragleave", onDragLeave);
        document.addEventListener("dragover", onDragOver);
        document.addEventListener("drop", onDrop);
        return () => {
            document.removeEventListener("dragenter", onDragEnter);
            document.removeEventListener("dragleave", onDragLeave);
            document.removeEventListener("dragover", onDragOver);
            document.removeEventListener("drop", onDrop);
        };
    }, []);
    // Persist filter/sort/group state whenever it changes. Scroll position is
    // saved separately right before navigation so we capture the moment of
    // departure rather than every scroll event.
    useEffect(() => {
        const current = readListState();
        writeListState({
            ...current,
            filter,
            query,
            sortKey,
            timeFilter,
            showTemplatesOnly,
            showNoTagsOnly,
            groupByCategory,
            activeTag,
        });
    }, [filter, query, sortKey, timeFilter, showTemplatesOnly, showNoTagsOnly, groupByCategory, activeTag]);
    // Restore scroll position after the list is rendered. Waits for readmes to
    // load so the page is tall enough for the saved scrollY to be reachable.
    useEffect(() => {
        if (readmes === null)
            return;
        const target = initialListState.scrollY;
        if (target <= 0)
            return;
        const frame = window.requestAnimationFrame(() => {
            window.scrollTo(0, target);
        });
        return () => window.cancelAnimationFrame(frame);
    }, [readmes, initialListState.scrollY]);
    function rememberScrollAndNavigate(to) {
        const current = readListState();
        writeListState({ ...current, scrollY: window.scrollY });
        navigate(to);
    }
    async function load() {
        try {
            const [readmesRes, skillsRes] = await Promise.all([
                api.listReadmes(),
                api.listSkills().catch(() => ({ skills: [] })),
            ]);
            setReadmes(readmesRes.readmes);
            setSkills(skillsRes.skills);
        }
        catch (err) {
            log.error("readmes.load", { err: String(err) });
            setLoadError(err instanceof ApiClientError ? err.message : "Failed to load.");
        }
    }
    async function createBlank() {
        setCreating(true);
        try {
            const { readme } = await api.createReadme({ title: "Untitled", content: "" });
            // `?blank=1` tells the editor to force edit mode and focus the editor
            // so the user can start typing without an extra tap.
            navigate(`/readmes/${readme.id}?blank=1`);
        }
        catch (err) {
            log.error("readmes.create", { err: String(err) });
            setLoadError(err instanceof ApiClientError ? err.message : "Failed to create.");
        }
        finally {
            setCreating(false);
        }
    }
    async function createFromWizard(data) {
        try {
            const { readme } = await api.createReadme(data);
            setWizardSeed(null);
            navigate(`/readmes/${readme.id}`);
        }
        catch (err) {
            log.error("readmes.create", { err: String(err) });
            throw err;
        }
    }
    function askDeleteSkill(skill) {
        setConfirmDelete({ kind: "skill", skill });
    }
    function askDelete(readme) {
        setConfirmDelete({ kind: "single", readme });
    }
    function askDeleteSelected() {
        if (selected.size === 0)
            return;
        setConfirmDelete({ kind: "bulk", ids: [...selected] });
    }
    async function performDelete() {
        if (!confirmDelete)
            return;
        setDeleting(true);
        try {
            if (confirmDelete.kind === "skill") {
                const skill = confirmDelete.skill;
                await api.deleteSkill(skill.id);
                setSkills((prev) => prev.filter((s) => s.id !== skill.id));
            }
            else if (confirmDelete.kind === "single") {
                const readme = confirmDelete.readme;
                await api.deleteReadme(readme.id);
                setReadmes((prev) => prev?.filter((r) => r.id !== readme.id) ?? prev);
            }
            else {
                const ids = confirmDelete.ids;
                const idSet = new Set(ids);
                await Promise.all(ids.map((id) => api.deleteReadme(id)));
                setReadmes((prev) => prev?.filter((r) => !idSet.has(r.id)) ?? prev);
                setSelected(new Set());
                setSelectMode(false);
            }
            setConfirmDelete(null);
        }
        catch (err) {
            log.error("readmes.delete", { err: String(err) });
            alert("Failed to delete.");
        }
        finally {
            setDeleting(false);
        }
    }
    async function handleCopy(readme) {
        // Pull the full body — the list summary doesn't include content. Falls
        // back to the title if the fetch fails so the action still does something
        // useful (e.g. offline).
        try {
            const { readme: full } = await api.getReadme(readme.id);
            await navigator.clipboard.writeText(full.content);
        }
        catch (err) {
            log.error("readmes.copy", { err: String(err) });
            try {
                await navigator.clipboard.writeText(displayTitle(readme.title));
            }
            catch {
                alert("Failed to copy.");
            }
        }
    }
    async function handleDownloadOne(readme) {
        try {
            const { readme: full } = await api.getReadme(readme.id);
            downloadText(`${sanitizeFilename(displayTitle(full.title))}.md`, full.content);
        }
        catch (err) {
            log.error("readmes.download", { err: String(err) });
            alert("Failed to download.");
        }
    }
    async function handleDownloadSelected() {
        if (selected.size === 0 || bulkDownloading)
            return;
        setBulkDownloading(true);
        try {
            const ids = [...selected];
            const fulls = await Promise.all(ids.map((id) => api.getReadme(id)));
            const entries = fulls.map(({ readme }) => ({
                name: `${sanitizeFilename(displayTitle(readme.title))}.md`,
                content: readme.content,
            }));
            const stamp = new Date().toISOString().slice(0, 10);
            const archiveName = entries.length === 1
                ? entries[0].name
                : `readmes-${stamp}-${entries.length}.zip`;
            if (entries.length === 1) {
                downloadText(entries[0].name, entries[0].content);
            }
            else {
                await downloadZip(archiveName, entries);
            }
        }
        catch (err) {
            log.error("readmes.bulkDownload", { err: String(err) });
            alert("Failed to download.");
        }
        finally {
            setBulkDownloading(false);
        }
    }
    function toggleSelect(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }
    function enterSelectMode() {
        setSelected(new Set());
        setSelectMode(true);
        setContextMenu(null);
    }
    function exitSelectMode() {
        setSelected(new Set());
        setSelectMode(false);
    }
    async function handleRename(readme, newTitle) {
        try {
            await api.updateReadme(readme.id, { title: newTitle });
            setReadmes((prev) => prev?.map((r) => r.id === readme.id ? { ...r, title: newTitle } : r) ?? prev);
        }
        catch (err) {
            log.error("readmes.rename", { err: String(err) });
            alert("Failed to rename.");
        }
        finally {
            setRenameTarget(null);
        }
    }
    function openContextMenu(readme, e) {
        let x = 0, y = 0;
        if ("clientX" in e) {
            x = e.clientX;
            y = e.clientY;
        }
        else if (e.touches && e.touches.length > 0) {
            x = e.touches[0].clientX;
            y = e.touches[0].clientY;
        }
        else if (e.changedTouches && e.changedTouches.length > 0) {
            x = e.changedTouches[0].clientX;
            y = e.changedTouches[0].clientY;
        }
        setContextMenu({ readme, x, y });
    }
    const cutoff = timeFilterCutoff(timeFilter);
    const filtered = useMemo(() => {
        if (!readmes)
            return [];
        const q = query.trim().toLowerCase();
        let list = readmes.filter((r) => {
            if (showTemplatesOnly && !r.isTemplate)
                return false;
            if (showNoTagsOnly && r.tags.length > 0)
                return false;
            if (filter === "none" && r.category)
                return false;
            if (filter !== "all" && filter !== "none" && r.category !== filter)
                return false;
            if (cutoff > 0 && r.updatedAt < cutoff)
                return false;
            if (q) {
                const hay = `${r.title} ${r.tags.join(" ")}`.toLowerCase();
                if (!hay.includes(q))
                    return false;
            }
            if (activeTag && !r.tags.includes(activeTag))
                return false;
            return true;
        });
        const pinned = list.filter((r) => r.isPinned);
        const rest = list.filter((r) => !r.isPinned);
        function sortFn(a, b) {
            if (sortKey === "az")
                return displayTitle(a.title).localeCompare(displayTitle(b.title));
            if (sortKey === "za")
                return displayTitle(b.title).localeCompare(displayTitle(a.title));
            if (sortKey === "created")
                return b.createdAt - a.createdAt;
            return b.updatedAt - a.updatedAt; // "updated" (default)
        }
        return [...pinned.sort(sortFn), ...rest.sort(sortFn)];
    }, [readmes, filter, query, showNoTagsOnly, showTemplatesOnly, sortKey, cutoff, activeTag]);
    const popularTags = useMemo(() => {
        const counts = new Map();
        for (const r of readmes ?? []) {
            for (const t of r.tags)
                counts.set(t, (counts.get(t) ?? 0) + 1);
        }
        return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
    }, [readmes]);
    const grouped = useMemo(() => {
        const map = new Map();
        for (const r of filtered) {
            const key = r.category ?? "Uncategorized";
            const arr = map.get(key) ?? [];
            arr.push(r);
            map.set(key, arr);
        }
        const entries = [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
        // Pre-compute which derived doc IDs to skip per group (rendered under template).
        return entries.map(([group, items]) => {
            const templateIdsInGroup = new Set(items.filter((r) => r.isTemplate).map((r) => r.id));
            const derivedIdsToSkip = new Set(items
                .filter((r) => {
                const pid = r.parentTemplateId;
                return pid !== null && templateIdsInGroup.has(pid);
            })
                .map((r) => r.id));
            return { group, items, derivedIdsToSkip };
        });
    }, [filtered]);
    // Map of templateId → all derived docs (from the full unfiltered list),
    // used to render derived docs nested under their template card.
    const derivedByTemplate = useMemo(() => {
        const map = new Map();
        for (const r of readmes ?? []) {
            if (r.parentTemplateId) {
                const arr = map.get(r.parentTemplateId) ?? [];
                arr.push(r);
                map.set(r.parentTemplateId, arr);
            }
        }
        return map;
    }, [readmes]);
    // For the flat list view: derived docs whose template is also in `filtered`
    // are rendered nested under the template, so we skip them here.
    const flatListItems = useMemo(() => {
        const templateIds = new Set(filtered.filter((r) => r.isTemplate).map((r) => r.id));
        const skipIds = new Set(filtered
            .filter((r) => r.parentTemplateId && templateIds.has(r.parentTemplateId))
            .map((r) => r.id));
        return filtered.filter((r) => !skipIds.has(r.id));
    }, [filtered]);
    function createCategory() {
        const candidate = newCategory.trim();
        if (!candidate)
            return;
        const exists = [...prefs.customCategories, ...READMECATEGORIES]
            .some((name) => name.toLowerCase() === candidate.toLowerCase());
        if (exists) {
            setNewCategory("");
            return;
        }
        setPref("customCategories", [...prefs.customCategories, candidate]);
        setFilter(candidate);
        setNewCategory("");
    }
    const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
    function toggleSelectAll() {
        if (allFilteredSelected) {
            setSelected((prev) => {
                const next = new Set(prev);
                filtered.forEach((r) => next.delete(r.id));
                return next;
            });
        }
        else {
            setSelected((prev) => {
                const next = new Set(prev);
                filtered.forEach((r) => next.add(r.id));
                return next;
            });
        }
    }
    return (_jsxs("div", { className: "pb-24 md:pb-0", children: [pageIsDragOver && (_jsx("div", { className: "pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-sky-500/10", children: _jsxs("div", { className: "rounded-2xl border-2 border-dashed border-sky-500 bg-white/90 px-10 py-8 text-center shadow-xl dark:bg-slate-900/90", children: [_jsx("svg", { width: "40", height: "40", viewBox: "0 0 24 24", fill: "none", className: "mx-auto mb-3 text-sky-500", "aria-hidden": true, children: _jsx("path", { d: "M9 17H7a5 5 0 010-10h.5M15 17h2a5 5 0 000-10h-.5M12 12V3m0 0l-3 3m3-3l3 3", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }), _jsx("p", { className: "text-base font-semibold text-sky-700 dark:text-sky-300", children: "Drop to import READMEs" })] }) })), _jsx("div", { className: "sticky top-12 z-20 -mx-3 mb-3 border-b border-slate-200 bg-white/95 px-3 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:top-14 sm:-mx-4 sm:px-4", children: _jsx("div", { className: "flex flex-wrap items-center gap-2", children: selectMode ? (_jsxs(_Fragment, { children: [_jsxs("span", { className: "mr-auto text-sm font-medium text-slate-700 dark:text-slate-200", children: [selected.size, " selected"] }), _jsx("button", { className: "btn-secondary text-sm", onClick: toggleSelectAll, children: allFilteredSelected ? "Deselect all" : "Select all" }), _jsx("button", { className: "hidden md:inline-flex btn-secondary text-sm disabled:opacity-50", onClick: () => void handleDownloadSelected(), disabled: selected.size === 0 || bulkDownloading, children: bulkDownloading ? "Preparing…" : `Download${selected.size > 0 ? ` ${selected.size}` : ""}` }), _jsx("button", { className: "hidden md:inline-flex btn-primary bg-rose-600 text-sm hover:bg-rose-500 focus:ring-rose-500 disabled:opacity-50", onClick: askDeleteSelected, disabled: selected.size === 0 || deleting, children: deleting ? "Deleting…" : `Delete ${selected.size > 0 ? selected.size : ""}` }), _jsx("button", { className: "btn-secondary text-sm", onClick: exitSelectMode, children: "Cancel" })] })) : (_jsxs(_Fragment, { children: [_jsx("h1", { className: "mr-auto text-2xl font-semibold", children: "READMEs" }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsx("button", { className: "btn-secondary", onClick: enterSelectMode, children: "Select" }), _jsx("button", { className: "btn-secondary", onClick: () => setShowImport(true), children: "Import" }), _jsx("button", { className: "btn-secondary", onClick: () => setShowSkillImport(true), children: "Import Skill" }), _jsx("button", { className: "hidden md:inline-flex btn-primary", onClick: () => setShowChooser(true), disabled: creating, children: creating ? "Creating…" : "+ New" })] })] })) }) }), _jsxs("div", { className: "mb-4 flex flex-wrap items-center gap-2", children: [_jsxs("div", { className: "relative w-full sm:w-56", children: [_jsxs("svg", { className: "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("circle", { cx: "11", cy: "11", r: "7", stroke: "currentColor", strokeWidth: "2" }), _jsx("path", { d: "M20 20l-3.5-3.5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })] }), _jsx("input", { className: "input pl-9", placeholder: "Search READMEs\u2026", value: query, onChange: (e) => setQuery(e.target.value) })] }), _jsxs("select", { className: "input w-auto", value: filter, onChange: (e) => setFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All READMEs" }), _jsx("option", { value: "none", children: "No category" }), allCategories.map((c) => (_jsx("option", { value: c, children: c }, c)))] }), showNewCategory ? (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("input", { className: "input w-36", placeholder: "New category\u2026", value: newCategory, autoFocus: true, onChange: (e) => setNewCategory(e.target.value), onKeyDown: (e) => {
                                    if (e.key === "Enter") {
                                        createCategory();
                                        setShowNewCategory(false);
                                    }
                                    if (e.key === "Escape")
                                        setShowNewCategory(false);
                                } }), _jsx("button", { className: "btn-secondary text-xs", onClick: () => {
                                    createCategory();
                                    setShowNewCategory(false);
                                }, children: "Add" })] })) : (_jsx("button", { className: "btn-ghost text-xs", onClick: () => setShowNewCategory(true), children: "+ Category" })), _jsxs("select", { className: "input w-auto", value: sortKey, onChange: (e) => setSortKey(e.target.value), children: [_jsx("option", { value: "updated", children: "Recently updated" }), _jsx("option", { value: "created", children: "Recently created" }), _jsx("option", { value: "az", children: "A \u2192 Z" }), _jsx("option", { value: "za", children: "Z \u2192 A" })] }), _jsx("div", { className: "flex overflow-hidden rounded-full border border-slate-300 dark:border-slate-700", children: ["all", "1h", "1d", "1w"].map((tf) => (_jsx("button", { onClick: () => setTimeFilter(tf), className: `px-3 py-1.5 text-xs font-medium ${timeFilter === tf
                                ? "bg-sky-600 text-white"
                                : "bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"}`, children: tf === "all" ? "Any time" : tf }, tf))) }), [
                        { label: "Templates only", active: showTemplatesOnly, toggle: () => setShowTemplatesOnly(!showTemplatesOnly) },
                        { label: "No tags", active: showNoTagsOnly, toggle: () => setShowNoTagsOnly(!showNoTagsOnly) },
                        { label: "Group by category", active: groupByCategory, toggle: () => setGroupByCategory(!groupByCategory) },
                    ].map(({ label, active, toggle }) => (_jsx("button", { "aria-pressed": active, onClick: toggle, className: `rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active
                            ? "border-sky-500 bg-sky-100 text-sky-700 dark:border-sky-400 dark:bg-sky-950/40 dark:text-sky-300"
                            : "border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"}`, children: label }, label)))] }), popularTags.length > 0 && (_jsxs("div", { className: "mb-4 flex flex-wrap items-center gap-2", children: [_jsx("span", { className: "text-xs uppercase tracking-wide text-slate-500", children: "Tags" }), popularTags.map(([tag, count]) => (_jsxs("button", { className: `rounded-full border px-2 py-0.5 text-xs ${activeTag === tag
                            ? "border-sky-500 bg-sky-100 text-sky-700 dark:border-sky-400 dark:bg-sky-950/40 dark:text-sky-300"
                            : "border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"}`, onClick: () => setActiveTag((prev) => (prev === tag ? null : tag)), children: ["#", tag, " (", count, ")"] }, tag))), activeTag && (_jsx("button", { className: "rounded-full border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800", onClick: () => setActiveTag(null), children: "Clear" }))] })), skills.length > 0 && (_jsxs("section", { className: "mb-6", children: [_jsxs("div", { className: "mb-3 flex items-center gap-3", children: [_jsx("h2", { className: "text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500", children: "Skills" }), _jsx("span", { className: "rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500 dark:bg-slate-800 dark:text-slate-400", children: skills.length }), _jsx("span", { className: "h-px flex-1 bg-slate-200 dark:bg-slate-800", "aria-hidden": true })] }), _jsx("ul", { className: "grid gap-3 sm:grid-cols-2 lg:grid-cols-3", children: skills.map((s) => (_jsx("li", { children: _jsx(SkillCard, { skill: s, onOpen: () => rememberScrollAndNavigate(`/skills/${s.id}`), onDelete: () => askDeleteSkill(s) }) }, s.id))) })] })), loadError && (_jsx("div", { className: "mb-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", children: loadError })), readmes === null ? (_jsx("div", { className: "grid gap-3 sm:grid-cols-2 lg:grid-cols-3", "aria-busy": "true", "aria-label": "Loading READMEs", children: Array.from({ length: 6 }).map((_, i) => (_jsxs("div", { className: "card animate-pulse p-4", children: [_jsx("div", { className: "mb-3 h-4 w-3/4 rounded bg-slate-200 dark:bg-slate-800" }), _jsx("div", { className: "mb-6 h-4 w-1/2 rounded bg-slate-200 dark:bg-slate-800" }), _jsxs("div", { className: "flex gap-1.5", children: [_jsx("div", { className: "h-5 w-16 rounded-full bg-slate-200 dark:bg-slate-800" }), _jsx("div", { className: "h-5 w-12 rounded-full bg-slate-200 dark:bg-slate-800" })] })] }, i))) })) : filtered.length === 0 ? (readmes.length === 0 ? (_jsxs("div", { className: "card p-10 text-center sm:p-14", children: [_jsx("div", { className: "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-sky-100 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400", children: _jsxs("svg", { width: "26", height: "26", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("path", { d: "M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z", stroke: "currentColor", strokeWidth: "1.8", strokeLinejoin: "round" }), _jsx("path", { d: "M14 3v5h5", stroke: "currentColor", strokeWidth: "1.8", strokeLinejoin: "round" }), _jsx("path", { d: "M9.5 13h5M9.5 16.5h5", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" })] }) }), _jsx("h2", { className: "text-base font-semibold text-slate-900 dark:text-slate-100", children: "Create your first README" }), _jsx("p", { className: "mt-1 text-sm text-slate-500", children: "Start from scratch or import existing markdown files." }), _jsxs("div", { className: "mt-5 flex justify-center gap-2", children: [_jsx("button", { className: "btn-primary", onClick: () => setShowChooser(true), children: "+ New README" }), _jsx("button", { className: "btn-secondary", onClick: () => setShowImport(true), children: "Import" })] })] })) : (_jsxs("div", { className: "card p-10 text-center sm:p-14", children: [_jsx("div", { className: "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400", children: _jsxs("svg", { width: "26", height: "26", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("circle", { cx: "11", cy: "11", r: "7", stroke: "currentColor", strokeWidth: "1.8" }), _jsx("path", { d: "M20 20l-3.5-3.5", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" })] }) }), _jsx("h2", { className: "text-base font-semibold text-slate-900 dark:text-slate-100", children: "No READMEs match your filters" }), _jsx("p", { className: "mt-1 text-sm text-slate-500", children: "Try a different search or clear the active filters." }), _jsx("div", { className: "mt-5 flex justify-center", children: _jsx("button", { className: "btn-secondary", onClick: () => {
                                setQuery("");
                                setFilter("all");
                                setTimeFilter("all");
                                setShowTemplatesOnly(false);
                                setShowNoTagsOnly(false);
                                setActiveTag(null);
                            }, children: "Clear filters" }) })] }))) : groupByCategory ? (_jsx("div", { className: "space-y-5", children: grouped.map(({ group, items, derivedIdsToSkip }) => (_jsxs("section", { children: [_jsxs("div", { className: "mb-3 flex items-center gap-3", children: [_jsx("h2", { className: "text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500", children: group }), _jsx("span", { className: "rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500 dark:bg-slate-800 dark:text-slate-400", children: items.length }), _jsx("span", { className: "h-px flex-1 bg-slate-200 dark:bg-slate-800", "aria-hidden": true })] }), _jsx("div", { className: "grid gap-3 sm:grid-cols-2 lg:grid-cols-3", children: items.filter((r) => !derivedIdsToSkip.has(r.id)).map((r) => (_jsxs("div", { className: r.isTemplate && !selectMode ? "sm:col-span-2 lg:col-span-3" : "", children: [_jsx(ReadmeCard, { readme: r, onOpen: () => rememberScrollAndNavigate(`/readmes/${r.id}`), onDelete: () => askDelete(r), onContextMenu: (e) => openContextMenu(r, e), selectMode: selectMode, isSelected: selected.has(r.id), onSelect: () => toggleSelect(r.id), derivedCount: r.isTemplate ? (derivedByTemplate.get(r.id)?.length ?? 0) : undefined }), r.isTemplate && !selectMode && (_jsx(TemplateDerivedRow, { items: sortReadmes(derivedByTemplate.get(r.id) ?? [], sortKey), onOpen: rememberScrollAndNavigate, onDelete: askDelete, onContextMenu: openContextMenu, selectMode: selectMode, selected: selected, onSelect: toggleSelect }))] }, r.id))) })] }, group))) })) : (_jsx("div", { className: "grid gap-3 sm:grid-cols-2 lg:grid-cols-3", children: flatListItems.map((r) => (_jsxs("div", { className: r.isTemplate && !selectMode ? "sm:col-span-2 lg:col-span-3" : "", children: [_jsx(ReadmeCard, { readme: r, onOpen: () => rememberScrollAndNavigate(`/readmes/${r.id}`), onDelete: () => askDelete(r), onContextMenu: (e) => openContextMenu(r, e), selectMode: selectMode, isSelected: selected.has(r.id), onSelect: () => toggleSelect(r.id), derivedCount: r.isTemplate ? (derivedByTemplate.get(r.id)?.length ?? 0) : undefined }), r.isTemplate && !selectMode && (_jsx(TemplateDerivedRow, { items: sortReadmes(derivedByTemplate.get(r.id) ?? [], sortKey), onOpen: rememberScrollAndNavigate, onDelete: askDelete, onContextMenu: openContextMenu, selectMode: selectMode, selected: selected, onSelect: toggleSelect }))] }, r.id))) })), contextMenu && (_jsx(ContextMenu, { state: contextMenu, isTouchUi: isTouchUi, onOpen: () => rememberScrollAndNavigate(`/readmes/${contextMenu.readme.id}`), onCopy: () => void handleCopy(contextMenu.readme), onDownload: () => void handleDownloadOne(contextMenu.readme), onRename: () => setRenameTarget(contextMenu.readme), onDelete: () => askDelete(contextMenu.readme), onClose: () => setContextMenu(null) })), renameTarget && (_jsx(RenameModal, { readme: renameTarget, onSave: (newTitle) => handleRename(renameTarget, newTitle), onClose: () => setRenameTarget(null) })), _jsx(ImportModal, { open: showImport, onClose: () => { setShowImport(false); setDroppedImportFiles(undefined); }, onImported: (summaries) => {
                    setReadmes((prev) => prev ? [...summaries, ...prev] : summaries);
                }, initialFiles: droppedImportFiles }), _jsx(NewReadmeChooserModal, { open: showChooser, onClose: () => setShowChooser(false), onChooseBlank: () => {
                    setShowChooser(false);
                    void createBlank();
                }, onChoosePaste: (text) => {
                    setShowChooser(false);
                    setWizardSeed(text);
                } }), wizardSeed !== null && (_jsx(MetadataWizardModal, { mode: "create", initialContent: wizardSeed, onClose: () => setWizardSeed(null), onCreate: createFromWizard })), showSkillImport && (_jsx(SkillImportModal, { onClose: () => setShowSkillImport(false), onImported: (skill) => {
                    setSkills((prev) => [skill, ...prev]);
                } })), _jsx("div", { className: "md:hidden", children: selectMode && selected.size > 0 ? (_jsx("div", { className: "fixed inset-x-0 bottom-0 z-30 animate-[slideUp_180ms_ease-out] border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur dark:border-slate-800 dark:bg-slate-900/95", children: _jsxs("div", { className: "flex items-center gap-2 px-3 py-3", children: [_jsx("button", { className: "btn-secondary flex-1 text-sm disabled:opacity-50", onClick: () => void handleDownloadSelected(), disabled: bulkDownloading, children: bulkDownloading ? "Preparing…" : `Download ${selected.size}` }), _jsx("button", { className: "btn-primary flex-1 bg-rose-600 text-sm hover:bg-rose-500 focus:ring-rose-500 disabled:opacity-50", onClick: askDeleteSelected, disabled: deleting, children: deleting ? "Deleting…" : `Delete ${selected.size}` })] }) })) : !selectMode ? (_jsx("div", { className: "fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur dark:border-slate-800 dark:bg-slate-900/95", children: _jsx("div", { className: "flex items-center justify-end px-3 py-2", children: _jsxs("button", { className: "btn-primary inline-flex items-center gap-1.5 px-5 py-2.5", onClick: () => setShowChooser(true), disabled: creating, children: [_jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M12 5v14M5 12h14", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round" }) }), _jsx("span", { children: creating ? "Creating…" : "New" })] }) }) })) : null }), confirmDelete && (_jsx(ConfirmModal, { title: confirmDelete.kind === "bulk"
                    ? `Delete ${confirmDelete.ids.length} README${confirmDelete.ids.length === 1 ? "" : "s"}?`
                    : confirmDelete.kind === "skill"
                        ? `Delete skill "${confirmDelete.skill.title}"?`
                        : `Delete "${displayTitle(confirmDelete.readme.title)}"?`, message: "This cannot be undone.", confirmLabel: confirmDelete.kind === "bulk" ? `Delete ${confirmDelete.ids.length}` : "Delete", busy: deleting, onConfirm: () => void performDelete(), onCancel: () => setConfirmDelete(null) }))] }));
}
